<script lang="ts">
  /**
   * The source list. `collapsible` is the web view — collapsed by default, with
   * the relationship records listed as openable rows. The doc view renders
   * numbered slices instead (one `<ol start>` per page), and prints only the
   * public citations: a page cannot open an email, and the basis line in the
   * footer is what discloses the relationship evidence there.
   */
  import type { RenderCtx, Source } from './types';

  let {
    items, ctx, startNumber = 1, collapsible = true, total = items.length,
    open = $bindable(false), highlighted = '', relationship = [],
  }: {
    items: Source[];
    ctx: RenderCtx;
    startNumber?: number;
    collapsible?: boolean;
    total?: number;
    open?: boolean;
    highlighted?: string;
    relationship?: Source[];
  } = $props();

  // A source URL is model or web text: anything but http(s) stays a plain label.
  const safeUrl = (url?: string) => (url && /^https?:\/\//i.test(url.trim()) ? url.trim() : '');
</script>

{#snippet list()}
  <ol class="brf-sources" start={startNumber}>
    {#each items as source (source.id)}
      <li id="{ctx.idPrefix}-{source.id}" class:hl={highlighted === source.id}>
        {#if safeUrl(source.url)}<a href={safeUrl(source.url)} target="_blank" rel="noopener noreferrer">{source.label}</a>
        {:else}{source.label}{/if}
        <!-- Printed too: a reviewer checks secondary figures before the briefing leaves the team. -->
        {#if source.as_of}<span class="k">{source.as_of}</span>{/if}
        {#if source.source_type === 'secondary'}<span class="k">secondary source</span>{/if}
      </li>
    {/each}
  </ol>
{/snippet}

{#if collapsible}
  <details class="brf-srcs" bind:open>
    <summary>Sources <span class="n">({total})</span></summary>
    {@render list()}
    {#if relationship.length}
      <p class="brf-srcs-sub">From our relationship</p>
      <ul class="brf-srcs-rel">
        {#each relationship as source (source.id)}
          <li id="{ctx.idPrefix}-{source.id}" class:hl={highlighted === source.id}>
            <!-- Only a source with a message id can be opened from here. A
                 meeting or a note stays a label until a host page has somewhere
                 to send it. -->
            {#if ctx.onOpenSource && source.message_id}
              <button type="button" class="brf-link"
                      onclick={() => ctx.onOpenSource?.(source)}>{source.label}</button>
            {:else}{source.label}{/if}
            <span class="k">{source.kind}</span>
          </li>
        {/each}
      </ul>
    {/if}
  </details>
{:else}
  <!-- Doc view. The heading is a separate flow item, so the caller renders it:
       it may land on the page before this slice. -->
  <div class="brf-srcs flat">{@render list()}</div>
{/if}
